// Optional data the UI shows when the services layer provides it, beyond today's contract in
// src/services/api.ts. Each accessor feature-detects and returns null when absent, so the HUD
// renders an honest "not provided" state instead of inventing anything.
//
// Contract requests (to the integrator):
//   1. WorldService.landMask() → { land, ice } per hex (minimap: unowned land such as
//      Antarctica must not read as ocean)
//   2. AppState.footprint → the four ground points under the screen corners, [lon, lat]
//      (minimap: camera trapezoid)
//   3. WorldService.controlSeries(warId) → dated share of territory held (war panel chart)
//   4. WorldService.relationHistory(iso3) → relation to the leader per month up to the date shown,
//      each with its reason and sources (diplomacy strip)
//   5. WorldService.flag(iso3) → URL of an openly licensed flag (medallions for states)
//   6. WorldService.spaceLadder(iso3) → rungs of the space ladder reached at the date shown and the
//      next one (victory screen; VictoryEntry cannot carry them)
//   7. WorldService.warView(warId) → the war as it stood on the date shown: dated status, intensity
//      only when dated ≤ the date, events and statements ≤ the date (war panel; War is curated at
//      the as-of date and leaks the future)
//   8. WorldService.actorInfo(id) → name, colours and kind of any actor, non-states included
//      (ribbon medallions, war sides; CountryReport covers states only)
//   9. HexDetail.controlFrac → share of the hex the controller holds, 0..255, and HexDetail.controlBasis →
//      how that is known: recorded, a map convention, or unknown (hover card)
//
// The services layer provides all nine (footprint through app/footprint.ts); a service without one
// leaves that spot empty or falls back to what the contract carries.

import type { ReadonlySignal } from '@preact/signals';
import type { AgendaItem, Claim, DateStr, LeaderId, SourceId, Span, Statement, War } from '../core/schema.ts';
import type { AppState, WorldService } from '../services/api.ts';
import type { HexDetail, Relation } from '../world/api.ts';

// ─── Perspective detail (in the contract; kept as one helper for the null cases) ─────────

interface PerspectiveDetail {
  term: Span | null;
  agenda: AgendaItem[];
}

export function perspectiveDetail(world: WorldService, id: LeaderId): PerspectiveDetail {
  const found = world.perspectives().find((p) => p.id === id);
  return {
    term: found?.term ?? null,
    agenda: Array.isArray(found?.agenda) ? found.agenda : [],
  };
}

// ─── 1. Land mask ────────────────────────────────────────────────────────────────────────

export interface LandMask {
  /** Land fraction per hex, 0..255. */
  land: Uint8Array;
  /** Glacier / ice-sheet fraction per hex, 0..255. */
  ice?: Uint8Array;
}

export function landMask(world: WorldService): LandMask | null {
  const api = world as WorldService & Partial<{ landMask(): LandMask | null }>;
  if (typeof api.landMask !== 'function') {
    return null;
  }
  return api.landMask();
}

// ─── 2. Camera footprint ─────────────────────────────────────────────────────────────────

/** Ground points under the four screen corners, [lon, lat], clockwise from top-left. */
export type Footprint = Array<[lon: number, lat: number]>;

export function footprintOf(state: AppState): Footprint | null {
  const extra = state as AppState & Partial<{ footprint: ReadonlySignal<Footprint | null> }>;
  return extra.footprint?.value ?? null;
}

// ─── 3. Control over time ────────────────────────────────────────────────────────────────

export interface ControlPoint {
  date: DateStr;
  /** 0..1 share of the pre-war territory held by the occupying side. */
  share: number;
  area_km2: number;
}

export interface ControlHistory {
  /** Who holds the territory, and whose territory it is. */
  controller: string;
  territoryOf: string;
  points: ControlPoint[];
  sources: SourceId[];
}

export function controlHistory(world: WorldService, warId: string): ControlHistory | null {
  const api = world as WorldService & Partial<{ controlSeries(warId: string): ControlHistory | null }>;
  if (typeof api.controlSeries !== 'function') {
    return null;
  }
  return api.controlSeries(warId);
}

// ─── 4. Relation history ─────────────────────────────────────────────────────────────────

export interface RelationMonth {
  /** 'YYYY-MM'. */
  month: string;
  relation: Relation;
  /** The rule that fired at that month's end. */
  reason: string;
  sources: SourceId[];
}

export function relationHistory(world: WorldService, iso3: string): RelationMonth[] | null {
  const api = world as WorldService & Partial<{ relationHistory(iso3: string): RelationMonth[] }>;
  if (typeof api.relationHistory !== 'function') {
    return null;
  }
  return api.relationHistory(iso3);
}

// ─── 5. Flags ────────────────────────────────────────────────────────────────────────────

export function flagOf(world: WorldService, iso3: string): string | null {
  const api = world as WorldService & Partial<{ flag(iso3: string): string | null }>;
  if (typeof api.flag !== 'function') {
    return null;
  }
  return api.flag(iso3);
}

// ─── 6. Space ladder ─────────────────────────────────────────────────────────────────────

export interface SpaceRung {
  rung: number;
  label: string;
  reached: { date: DateStr; title: string; sources: SourceId[] } | null;
}

export interface SpaceLadder {
  reached: number;
  total: number;
  /** The lowest rung not reached yet; null when all are. */
  next: { rung: number; label: string } | null;
  rungs: SpaceRung[];
}

export function spaceLadderOf(world: WorldService, iso3: string): SpaceLadder | null {
  const api = world as WorldService & Partial<{ spaceLadder(iso3: string): SpaceLadder }>;
  if (typeof api.spaceLadder !== 'function') {
    return null;
  }
  return api.spaceLadder(iso3);
}

// ─── 7. War at a date ────────────────────────────────────────────────────────────────────

export interface WarView {
  war: War;
  status: Claim<string>;
  /** Null when the curated claim is dated after the date shown. */
  intensity: Claim<string> | null;
  /** Dated ≤ the date shown, newest first. */
  events: War['events'];
  /** By any party, dated ≤ the date shown, newest first. */
  statements: Statement[];
  /** The end date, once it has passed. */
  ended: DateStr | null;
}

/** The war at the date shown; null when the service does not provide it or the war had not begun. */
export function warViewOf(world: WorldService, warId: string): WarView | null {
  const api = world as WorldService & Partial<{ warView(warId: string): WarView | null }>;
  if (typeof api.warView !== 'function') {
    return null;
  }
  return api.warView(warId);
}

// ─── 8. Any actor ────────────────────────────────────────────────────────────────────────

export interface ActorFacts {
  id: string;
  name: string;
  colors: { primary: string; secondary: string };
  /** 'country' for a state with a report; the schema's actor kinds otherwise. */
  kind: string;
}

export function actorInfoOf(world: WorldService, id: string): ActorFacts | null {
  const api = world as WorldService & Partial<{ actorInfo(id: string): ActorFacts | null }>;
  if (typeof api.actorInfo !== 'function') {
    return null;
  }
  return api.actorInfo(id);
}

// ─── 9. Held share of a hex ──────────────────────────────────────────────────────────────

/** Share of the hex the controller holds (0..255), or null when the detail does not carry it. */
export function heldFrac(detail: HexDetail): number | null {
  const frac = (detail as HexDetail & { controlFrac?: number }).controlFrac;
  return typeof frac === 'number' ? frac : null;
}

/** How a hex's held share is known: a source records it, the map assumes it (a partly held unit), or nobody observed. */
export type HeldBasis = 'recorded' | 'convention' | 'unknown';

const HELD_BASES: readonly string[] = ['recorded', 'convention', 'unknown'];

/** How the hex's held share is known, or null when the detail does not carry it. */
export function heldBasis(detail: HexDetail): HeldBasis | null {
  const basis = (detail as HexDetail & { controlBasis?: unknown }).controlBasis;
  return typeof basis === 'string' && HELD_BASES.includes(basis) ? (basis as HeldBasis) : null;
}
